import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, CheckCheck, Inbox, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import NotificationItem from "@/components/notifications/NotificationItem";
import {
  BELL_PREVIEW_LIMIT,
  getNotificationsPagePath,
  resolveNotificationUrl,
} from "@/lib/notificationRoutes";

export default function NotificationBell({ userRole }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await api.get("/notifications", { params: { limit: BELL_PREVIEW_LIMIT } });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      /* silencieux */
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.notification_id === id ? { ...n, is_read: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch {
      /* silencieux */
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await api.post("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      /* silencieux */
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.notification_id);
    }
    const url = resolveNotificationUrl(notification, userRole);
    setOpen(false);
    navigate(url);
  };

  const previewNotifications = notifications.slice(0, BELL_PREVIEW_LIMIT);
  const allNotificationsPath = getNotificationsPagePath(userRole);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notif-bell-btn">
          <Bell className="w-5 h-5 text-slate-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(100vw-2rem,24rem)] p-0 shadow-xl border-slate-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/80">
          <div>
            <h3 className="font-semibold text-sm text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <p className="text-[11px] text-slate-500">{unreadCount} non lue{unreadCount > 1 ? "s" : ""}</p>
            )}
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              disabled={loading}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1 disabled:opacity-50"
            >
              <CheckCheck className="w-3.5 h-3.5" /> Tout lire
            </button>
          )}
        </div>

        <div className="max-h-[min(70vh,22rem)] overflow-y-auto">
          {previewNotifications.length === 0 ? (
            <div className="py-12 text-center px-4">
              <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">Aucune notification</p>
              <p className="text-xs text-slate-300 mt-1">Vous serez alerté ici des nouveautés</p>
            </div>
          ) : (
            previewNotifications.map((n) => (
              <NotificationItem key={n.notification_id} notification={n} onClick={handleClick} compact />
            ))
          )}
        </div>

        <button
          type="button"
          onClick={() => {
            setOpen(false);
            navigate(allNotificationsPath);
          }}
          className="w-full flex items-center justify-center gap-1.5 py-3 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-slate-50 border-t border-slate-100 transition-colors"
        >
          Voir toutes les notifications
          <ChevronRight className="w-4 h-4" />
        </button>
      </PopoverContent>
    </Popover>
  );
}